import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useCodePipelinesQuery } from './useCodePipelinesQuery';
import { useCodePipelinesSearch } from './useCodePipelinesSearch';
import { useCodePipelinesFilter } from './useCodePipelinesFilter';
import { CodePipeline } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// `created` is NULLABLE on CodePipeline (types.ts), which is not what the
// library's guidance prefers for a default key: a nullable key degrades to
// insertion order if EVERY row lacks it. The alternative here is `name`, the
// only non-null field on the type, and defaulting an operator's first view to
// alphabetical hides the pipeline that just failed.
//
// `created` is the right key despite that, for the same reasons it was chosen
// for Pipeline under FNP-1322. It is immutable, unlike `updated`, which would
// reorder the list under someone reading it, and rows missing it sort last.
//
// If CodePipeline.created is ever made non-null in the API schema, this comment
// is the only thing that needs to change.
const DEFAULT_SORT_STATUS: DataTableSortStatus<CodePipeline> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useCodePipelines = () => {
    const query = useCodePipelinesQuery();
    const { keyword } = useCodePipelinesSearch();
    const { filters } = useCodePipelinesFilter();

    const items: CodePipeline[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // The keyword filter, the status filter and the sort below all cover only
    // the pages FETCHED SO FAR. Under token pagination the browser cannot
    // filter or reorder rows it has never seen, so the footer states a LOADED
    // count rather than a total, and `unfilteredCount` travels with the match
    // count so the operator can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item => {
        const matchesKeyword =
            !needle ||
            item.name?.toLowerCase().includes(needle) ||
            item.cloudFormationStackName?.toLowerCase().includes(needle) ||
            item.lastExecutionStatus?.toLowerCase().includes(needle);

        const matchesStatus =
            !filters.lastExecutionStatus ||
            item.lastExecutionStatus === filters.lastExecutionStatus;

        return matchesKeyword && matchesStatus;
    });

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<CodePipeline>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
